import React, { useEffect, useState } from 'react';

import styles from './AssetNetworkView.module.scss';
import { perfTestStart, perfTestStop } from '../../data/analyticsDataStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TpAssetNetwork,
  TpAssetNetworkNode,
  TpAssetNetworkRelation,
  createAssetNetwork,
  createAssetNode,
  layoutAssetNetworkDagre,
} from './assetNetwork';
import { useAppSelector } from '../../app/hooks';
import { TpQueriedAssetsData, selectQueriedAssets } from '../../data/assets/queriedAssetsSlice';
import { getAssetTypeName, useOpenAsset } from '../../data/assets/helpers';
import { selectTemplatesAndCombinedOntology } from '../../data/templateSlice';
import { Ontology } from '../../api/models/assets';

// interface TpAssetNetworkViewProps {}

function getEdgePath(network: TpAssetNetwork, edge: TpAssetNetworkRelation): string {
  const node1 = edge.node1;
  const node2 = edge.node2;
  const offs = network.horSep * 0.7;
  const startX = node1.xPos + node1.width - 20;
  const startY = node1.yPos + node1.height / 2;
  const endX = node2.xPos + 20;
  const endY = node2.yPos + node2.height / 2;
  return `M ${startX},${startY} C ${startX + offs},${startY} ${endX - offs},${endY} ${endX},${endY}`;
}

function getAssetTypeIcon(node: TpAssetNetworkNode): any {
  // @todo: rework this mockup
  if (node.typeName == 'Single-omics') return 'table';
  if (node.typeName == 'Multi-omics') return 'table-cells';
  if (node.typeName == 'Stored selection') return 'list';
  if (node.typeName == 'ML model') return 'gear';
  if (node.typeName == 'Dashboard') return 'chart-area';
  return 'book';
}

function getAssetNodeStyle(node: TpAssetNetworkNode): any {
  // @todo: rework this mockup
  if (node.typeName == 'Multi-omics' || node.typeName == 'ML model') return styles.assetNodeImportant;
  if (node.typeName == 'Stored selection' || node.typeName == 'Dashboard') return styles.assetNodeUnimportant;
  return styles.assetNode;
}

function createNetworkFromQueriedAssets(queriedAssetsData: TpQueriedAssetsData, ontology: Ontology): TpAssetNetwork {
  const network = createAssetNetwork(200, 32, 60, 7);

  // NOTE: this will have to change, as we in general don't want to show the network for all assets
  const nodes: { [assetId: string]: TpAssetNetworkNode } = {};
  for (const asset of queriedAssetsData.fetchedAssets) {
    nodes[asset.id] = createAssetNode(
      network,
      asset.displayName,
      getAssetTypeName(ontology.assetTypes[asset.type]),
      asset.id,
      [],
    );
  }
  for (const asset of queriedAssetsData.fetchedAssets) {
    for (const rel of asset.relations) {
      network.relations.push({
        node1: nodes[asset.id],
        node2: nodes[rel.to],
      });
    }
  }

  return network;
}

interface TpAssetNetworkViewProps {
  scope: string;
}

function AssetNetworkView(props: TpAssetNetworkViewProps) {
  const [network, setNetwork] = useState<TpAssetNetwork | null>(null);
  const queriedAssetsData = useAppSelector(selectQueriedAssets);
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const theNetwork = createNetworkFromQueriedAssets(queriedAssetsData, ontology);
  const openAsset = useOpenAsset();

  if (props.scope != 'all') throw 'Invalid network scope';

  useEffect(() => {
    const pf = perfTestStart();
    layoutAssetNetworkDagre(theNetwork);
    //layoutAssetNetworkFast(network);
    perfTestStop(pf, 'Graph layout');
    setNetwork(theNetwork);
  }, []);

  if (!network) return <div>Preparing network...</div>;

  return (
    <div style={{ fontSize: '80%' }}>
      <div style={{ height: '20px', margin: '20px 40px', borderTop: '1px solid rgba(0,0,0,0.20)' }} />
      <div className={styles.wrapper} style={{ width: `${network.totWidth}px`, height: `${network.totHeight}px` }}>
        <svg style={{ position: 'absolute', width: `${network.totWidth}px`, height: `${network.totHeight}px` }}>
          {network.relations.map((edge, idx) => (
            <React.Fragment key={`edge_${idx}`}>
              <path className={styles.relationsViewPathBg} d={getEdgePath(network, edge)} />
              <path className={styles.relationsViewPath} d={getEdgePath(network, edge)} />
            </React.Fragment>
          ))}
        </svg>
        <div style={{ position: 'absolute', width: `${network.totWidth}px`, height: `${network.totHeight}px` }}>
          {network.assets.map((node) => (
            <div
              key={node.id}
              className={getAssetNodeStyle(node)}
              style={{
                left: `${node.xPos}px`,
                top: `${node.yPos}px`,
                width: `${node.width}px`,
                height: `${node.height}px`,
              }}
              onClick={() => {
                openAsset(node.assetId);
              }}
            >
              <div className={styles.assetIcon}>
                <FontAwesomeIcon icon={['fas', getAssetTypeIcon(node)]} />
              </div>
              <div className={styles.assetTypeName}>{node.typeName}</div>
              <div className={styles.assetName}>{node.name}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ height: '60px' }} />
    </div>
  );
}

export default AssetNetworkView;
